import { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  getAllHospitals, 
  getAllPaymentHistory, 
  activateHospitalSubscription,
  getSubscriptionStats,
  type HospitalWithSubscription,
  type AllPaymentHistory
} from '@/lib/superAdmin';
import { getSubscriptionPlans } from '@/lib/subscription';
import type { SubscriptionPlan } from '@/lib/subscription';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { 
  Building2, 
  DollarSign, 
  Users, 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertCircle,
  Search,
  RefreshCw,
  CreditCard,
  Package
} from 'lucide-react';
import { SubscriptionPlansManagement } from '@/components/settings/SubscriptionPlansManagement';

const SuperAdminDashboard = () => {
  const { toast } = useToast();
  const [hospitals, setHospitals] = useState<HospitalWithSubscription[]>([]);
  const [payments, setPayments] = useState<AllPaymentHistory[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    trial: 0,
    active: 0,
    expired: 0,
    suspended: 0,
    cancelled: 0,
    totalRevenue: 0,
    totalPayments: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedHospital, setSelectedHospital] = useState<HospitalWithSubscription | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activeTab, setActiveTab] = useState<'hospitals' | 'payments' | 'plans'>('hospitals');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [hospitalsData, paymentsData, plansData, statsData] = await Promise.all([
        getAllHospitals(),
        getAllPaymentHistory(),
        getSubscriptionPlans(),
        getSubscriptionStats()
      ]);
      setHospitals(hospitalsData);
      setPayments(paymentsData);
      setPlans(plansData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredHospitals = hospitals.filter(hospital => {
    const matchesSearch = hospital.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hospital.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || hospital.subscription_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { variant: any; className: string; icon: any; label: string }> = {
      trial: { variant: 'outline', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock, label: 'Trial' },
      active: { variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle, label: 'Active' },
      expired: { variant: 'outline', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, label: 'Expired' },
      suspended: { variant: 'outline', className: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle, label: 'Suspended' },
      cancelled: { variant: 'outline', className: 'bg-gray-50 text-gray-700 border-gray-200', icon: XCircle, label: 'Cancelled' }
    };
    
    const config = configs[status] || configs.expired;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getDaysRemaining = (hospital: HospitalWithSubscription): number => {
    if (hospital.subscription_status === 'active' && hospital.subscription_expires_at) {
      const expiry = new Date(hospital.subscription_expires_at);
      const now = new Date();
      const diff = expiry.getTime() - now.getTime();
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    }
    if (hospital.subscription_status === 'trial' && hospital.trial_started_at) {
      const trialEnd = new Date(hospital.trial_started_at);
      trialEnd.setDate(trialEnd.getDate() + hospital.trial_days);
      const now = new Date();
      const diff = trialEnd.getTime() - now.getTime();
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const handleActivateSubscription = async () => {
    if (!selectedHospital || !selectedPlan) {
      toast({
        title: 'Error',
        description: 'Please select a hospital and plan',
        variant: 'destructive'
      });
      return;
    }

    setActivating(true);
    try {
      const success = await activateHospitalSubscription(selectedHospital.id, selectedPlan, 'manual');
      if (success) {
        toast({
          title: 'Success',
          description: 'Subscription activated successfully'
        });
        setActivateDialogOpen(false);
        await loadData();
      } else {
        throw new Error('Activation failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to activate subscription',
        variant: 'destructive'
      });
    } finally {
      setActivating(false);
    }
  };

  const openActivateDialog = (hospital: HospitalWithSubscription) => {
    setSelectedHospital(hospital);
    setSelectedPlan('');
    setActivateDialogOpen(true);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
            <p className="text-muted-foreground">Monitor all hospitals and subscriptions</p>
          </div>
          <Button onClick={loadData} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Hospitals</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Trial</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.trial}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{stats.totalRevenue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">{stats.totalPayments} payments</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <Button
            variant={activeTab === 'hospitals' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('hospitals')}
          >
            <Building2 className="h-4 w-4 mr-2" />
            Hospitals ({hospitals.length})
          </Button>
          <Button
            variant={activeTab === 'payments' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('payments')}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Payments ({payments.length})
          </Button>
          <Button
            variant={activeTab === 'plans' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('plans')}
          >
            <Package className="h-4 w-4 mr-2" />
            Subscription Plans ({plans.length})
          </Button>
        </div>

        {/* Hospitals Tab */}
        {activeTab === 'hospitals' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>All Hospitals</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search hospitals..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 w-64"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hospital Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Days Remaining</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHospitals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          No hospitals found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredHospitals.map((hospital) => {
                        const daysRemaining = getDaysRemaining(hospital);
                        return (
                          <TableRow key={hospital.id}>
                            <TableCell className="font-medium">{hospital.name}</TableCell>
                            <TableCell>{hospital.email || '-'}</TableCell>
                            <TableCell>{hospital.phone || '-'}</TableCell>
                            <TableCell>{getStatusBadge(hospital.subscription_status)}</TableCell>
                            <TableCell>
                              {daysRemaining > 0 ? (
                                <span className="text-sm">{daysRemaining} days</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">Expired</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {hospital.subscription_expires_at
                                ? format(new Date(hospital.subscription_expires_at), 'dd MMM yyyy')
                                : hospital.trial_started_at
                                ? format(new Date(new Date(hospital.trial_started_at).getTime() + hospital.trial_days * 24 * 60 * 60 * 1000), 'dd MMM yyyy')
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  hospital.payment_status === 'paid'
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-gray-50 text-gray-700 border-gray-200'
                                }
                              >
                                {hospital.payment_status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openActivateDialog(hospital)}
                              >
                                Activate
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Hospital</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Payment Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Transaction ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No payments found
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                          </TableCell>
                          <TableCell className="font-medium">{payment.hospital_name}</TableCell>
                          <TableCell>{payment.plan_name}</TableCell>
                          <TableCell>₹{Number(payment.amount).toLocaleString()}</TableCell>
                          <TableCell className="capitalize">{payment.payment_method}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                payment.status === 'completed'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : payment.status === 'failed'
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : 'bg-gray-50 text-gray-700 border-gray-200'
                              }
                            >
                              {payment.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {payment.transaction_id || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription Plans Tab */}
        {activeTab === 'plans' && <SubscriptionPlansManagement />}

        {/* Activate Subscription Dialog */}
        <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Activate Subscription</DialogTitle>
              <DialogDescription>
                Activate subscription for {selectedHospital?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Select Plan</label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - ₹{plan.price.toLocaleString()} ({plan.duration_months} months)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActivateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleActivateSubscription}
                  disabled={!selectedPlan || activating}
                >
                  {activating ? 'Activating...' : 'Activate'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default SuperAdminDashboard;

